import React, { useCallback, useEffect, useState } from "react";
import { useEscape } from "@/hooks/useEscape";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderGit2, Plus, Trash2, X, Bot, Archive, ArchiveRestore, Pencil } from "lucide-react";

// Mirrors PROJECT_SLUG_RE on the server. The slug is not cosmetic: it is the only
// thing the host runner uses to locate a project's checkout, so it is shown
// explicitly rather than hidden behind the name.
const SLUG_RE = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;

const slugify = (name) =>
  name.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase()
      .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 63);

// Regula serverului, peste datele serverului: o gazdă poate porni un agent dacă
// e activabilă și dacă slug-ul e cel pe care îl servește (un canal fixat pe un
// singur checkout nu servește alt proiect). Nimic despre gazde nu e scris aici —
// lista vine din /agent-hosts, ca să nu existe o a doua listă în JavaScript.
export const hostLabel = (hosts, key) => {
  const h = hosts.find((x) => x.key === key);
  return h ? `${h.label} · ` : "";
};

export const hostCanEnable = (host, slug) =>
  !!host && host.enablable && (host.pinned_slug === null || host.pinned_slug === slug);

function ProjectModal({ project, workspaceId, hosts, onClose, onSaved }) {
  useEscape(true, onClose);
  const editing = !!project;
  const [name, setName] = useState(project?.name || "");
  const [slug, setSlug] = useState(project?.slug || "");
  const [slugTouched, setSlugTouched] = useState(editing);
  const [description, setDescription] = useState(project?.description || "");
  const [agentHost, setAgentHost] = useState(project?.agent_host || "");
  const [agentBranch, setAgentBranch] = useState(project?.agent_branch || "main");
  const [agentRepoHint, setAgentRepoHint] = useState(project?.agent_repo_hint || "");
  const [agentEnabled, setAgentEnabled] = useState(!!project?.agent_enabled);
  const [saving, setSaving] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugOk = SLUG_RE.test(effectiveSlug);
  const host = hosts.find((h) => h.key === agentHost) || null;
  // The path is derived server-side from the slug and the host's root; showing it
  // here makes clear that nobody types a path into this form.
  const derivedPath = host ? `${host.checkout_root}/${effectiveSlug || "…"}` : null;
  const canEnable = hostCanEnable(host, effectiveSlug);

  useEffect(() => { if (!canEnable) setAgentEnabled(false); }, [canEnable]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Numele e obligatoriu");
    if (!editing && !slugOk) return toast.error("Slug invalid (3-64 caractere, a-z, 0-9, -)");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), description: description.trim(),
        agent_host: agentHost || null, agent_branch: agentBranch.trim() || "main",
        agent_repo_hint: agentHost ? agentRepoHint.trim() : null,
        agent_enabled: agentEnabled,
      };
      const { data } = editing
        ? await api.patch(`/ticket-projects/${project.id}`, payload, { params: { workspace_id: workspaceId } })
        : await api.post("/ticket-projects", { ...payload, slug: effectiveSlug, workspace_id: workspaceId });
      toast.success(editing ? "Proiect actualizat" : `Proiect „${data.name}” creat`);
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onMouseDown={onClose} data-testid="ticket-project-modal">
      <form onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-lg bg-surface border border-line/10 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="h-12 px-4 flex items-center justify-between border-b border-line/10 sticky top-0 bg-surface">
          <div className="flex items-center gap-2">
            <FolderGit2 className="w-4 h-4 text-brand" />
            <h3 className="font-bold tracking-tight">{editing ? `Editează „${project.name}”` : "Proiect nou"}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-fg/50 hover:text-fg"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label htmlFor="tp-name" className="label-tiny block mb-1.5">Nume *</label>
            <input id="tp-name" data-testid="ticket-project-name" value={name} autoFocus
                   onChange={(e) => setName(e.target.value)} className="input-flat"
                   placeholder="Qedara Contract Management" required />
          </div>

          <div>
            <label htmlFor="tp-slug" className="label-tiny block mb-1.5">
              Slug {editing ? "(fix după creare)" : "*"}
            </label>
            <input id="tp-slug" data-testid="ticket-project-slug" value={effectiveSlug}
                   onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
                   className="input-flat mono text-sm" disabled={editing} />
            {!editing && !slugOk && effectiveSlug && (
              <p className="text-[11px] text-destructive mt-1">3-64 caractere: litere mici, cifre, cratime.</p>
            )}
          </div>

          <div>
            <label htmlFor="tp-desc" className="label-tiny block mb-1.5">Descriere</label>
            <textarea id="tp-desc" value={description} onChange={(e) => setDescription(e.target.value)}
                      rows={2} className="input-flat resize-none text-sm" placeholder="La ce folosește produsul" />
          </div>

          <div className="border-t border-line/10 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Bot className="w-3.5 h-3.5 text-brand-2" />
              <span className="label-tiny">Claude Code</span>
            </div>

            <label htmlFor="tp-host" className="label-tiny block mb-1.5">Server</label>
            <select id="tp-host" data-testid="ticket-project-host" value={agentHost}
                    onChange={(e) => setAgentHost(e.target.value)} className="input-flat">
              <option value="">Fără agent</option>
              {hosts.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
            </select>

            {agentHost && (
              <div className="mt-3">
                <label htmlFor="tp-branch" className="label-tiny block mb-1.5">Branch</label>
                <input id="tp-branch" value={agentBranch} onChange={(e) => setAgentBranch(e.target.value)}
                       className="input-flat mono text-sm" placeholder="main" />
              </div>
            )}

            {host && (
              <>
                <div className="mt-3 bg-surface-2 border border-line/10 p-2.5" data-testid="ticket-project-checkout">
                  <p className="label-tiny mb-1">Checkout</p>
                  <p className="text-[11px] text-fg/50 mb-1">{host.label}</p>
                  <code className="mono text-xs text-brand break-all">{derivedPath}</code>
                  <p className="text-[11px] text-fg/40 mt-1.5 leading-relaxed">
                    Calea e derivată din slug, nu se poate scrie de mână. {host.note}
                  </p>
                </div>
                {host.pinned_slug && host.pinned_slug !== effectiveSlug && (
                  <p className="text-[11px] text-destructive mt-2" data-testid="ticket-project-host-pinned">
                    Agentul rămâne oprit: gazda aleasă servește doar checkout-ul {host.pinned_slug}
                  </p>
                )}
                <label className={`flex items-start gap-2 mt-3 ${canEnable ? "cursor-pointer" : "opacity-50"}`}>
                  <input type="checkbox" data-testid="ticket-project-enabled" checked={agentEnabled}
                         disabled={!canEnable}
                         onChange={(e) => setAgentEnabled(e.target.checked)} className="mt-0.5" />
                  <span className="text-sm">
                    Permite „Fix cu Claude Code” pe ticketele acestui proiect
                    <span className="block text-[11px] text-fg/40">
                      Agentul editează working tree-ul ca utilizator neprivilegiat. Nu comite și nu face push.
                    </span>
                  </span>
                </label>
              </>
            )}

            {host && (
              <div className="mt-3">
                <label htmlFor="tp-hint" className="label-tiny block mb-1.5">Notă despre repo (informativ)</label>
                <input id="tp-hint" value={agentRepoHint} onChange={(e) => setAgentRepoHint(e.target.value)}
                       className="input-flat mono text-sm" placeholder="ce conține checkout-ul" />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="text-sm text-fg/50 hover:text-fg px-3 py-2">Anulează</button>
            <button type="submit" disabled={saving} data-testid="ticket-project-submit"
                    className="btn-primary text-sm flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> {saving ? "Se salvează…" : editing ? "Salvează" : "Creează"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function TicketProjectsSettings({ workspaceId, isOwner }) {
  const { ask, confirmProps } = useConfirm();
  const [projects, setProjects] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null);   // project | "new" | null

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await api.get("/ticket-projects", {
        params: { workspace_id: workspaceId, include_archived: true },
      });
      setProjects(r.data || []);
    } catch { /* non-fatal: the section just stays empty */ }
    finally { setLoaded(true); }
  }, [workspaceId]);

  // Gazdele sunt date de server, nu o listă scrisă aici. Cererea e separată de
  // cea de proiecte: dacă una eșuează, cealaltă rămâne întreagă.
  useEffect(() => {
    let alive = true;
    api.get("/agent-hosts")
      .then((r) => { if (alive) setHosts(r.data?.hosts || []); })
      .catch(() => { /* meniul rămâne pe „Fără agent" */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => { load(); }, [load]);

  const setArchived = async (p, archived) => {
    try {
      await api.patch(`/ticket-projects/${p.id}`, { archived }, { params: { workspace_id: workspaceId } });
      toast.success(archived ? "Proiect arhivat" : "Proiect reactivat");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a putut schimba"); }
  };

  const remove = (p) => {
    ask({
      title: `Ștergi proiectul „${p.name}"?`, destructive: true, confirmLabel: "Șterge",
      description: "Proiectul dispare din picker; tichetele deja deschise pe el rămân.",
      testId: "confirm-delete-project", onConfirm: () => doRemove(p),
    });
  };

  const doRemove = async (p) => {
    try {
      await api.delete(`/ticket-projects/${p.id}`, { params: { workspace_id: workspaceId } });
      toast.success("Proiect șters");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu s-a putut șterge"); }
  };

  if (!isOwner) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="ticket-projects-section">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <FolderGit2 className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Proiecte</h3>
        <span className="label-tiny ml-2">// TICHETE</span>
        <button type="button" data-testid="ticket-project-create-button" onClick={() => setEditing("new")}
                className="ml-auto btn-primary text-xs flex items-center gap-1.5 px-2.5 py-1">
          <Plus className="w-3.5 h-3.5" /> Proiect nou
        </button>
      </div>

      <div className="p-5">
        <p className="text-xs text-fg/50 leading-relaxed mb-4">
          Produsele pe care se deschid tichete. Un proiect legat de acest server poate primi
          „Fix cu Claude Code” direct din ticket.
        </p>

        {!loaded ? (
          <div className="py-8 text-center label-tiny text-brand">se încarcă<span className="cursor-blink">_</span></div>
        ) : projects.length === 0 ? (
          <div className="py-6 text-center text-sm text-fg/40">
            <p className="label-tiny mb-1">// NICIUN PROIECT</p>
            <p>Creează primul ca să poți deschide tichete pe el.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="ticket-project-list">
            {projects.map((p) => (
              <div key={p.id} data-testid={`ticket-project-row-${p.id}`}
                   className={`bg-surface-2 border border-line/10 p-3 flex items-start gap-3 ${p.archived ? "opacity-50" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold truncate">{p.name}</span>
                    <code className="text-[10px] mono px-1.5 py-0.5 rounded-sm bg-fg/10 text-fg/60">{p.slug}</code>
                    {p.archived && <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-fg/10 text-fg/60">arhivat</span>}
                    {p.can_run_agent && (
                      <span className="text-[10px] mono px-1.5 py-0.5 rounded-sm bg-brand/15 text-brand flex items-center gap-1">
                        <Bot className="w-3 h-3" /> Claude Code
                      </span>
                    )}
                  </div>
                  {p.description && <p className="text-xs text-fg/60 mt-1">{p.description}</p>}
                  <p className="text-[11px] text-fg/40 mono mt-1.5 break-all">
                    {p.repo_path
                      ? <>{hostLabel(hosts, p.agent_host)}{p.repo_path} · {p.agent_branch}</>
                      : "fără agent"}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button type="button" onClick={() => setEditing(p)} title="Editează"
                          aria-label={`Editează ${p.name}`} className="text-fg/40 hover:text-fg p-1">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => setArchived(p, !p.archived)}
                          title={p.archived ? "Reactivează" : "Arhivează"}
                          aria-label={`${p.archived ? "Reactivează" : "Arhivează"} ${p.name}`}
                          className="text-fg/40 hover:text-fg p-1">
                    {p.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  </button>
                  <button type="button" onClick={() => remove(p)} title="Șterge"
                          aria-label={`Șterge ${p.name}`} className="text-fg/40 hover:text-destructive p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ProjectModal
          project={editing === "new" ? null : editing}
          workspaceId={workspaceId}
          hosts={hosts}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      <ConfirmDialog {...confirmProps} />
    </section>
  );
}
